import React from 'react';
import { Box, Typography, Button, Link as MuiLink } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import useAdaptiveFontSize from './hooks/useAdaptiveFontSize';
import { BrandLockup } from "components";
import logging from 'utils/logging';

const log = logging.getLogger('GameCompleteChoice');

const Root = styled(Box)({
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '48px 24px',
  '&::before': {
    content: '""',
    position: 'fixed',
    inset: 0,
    background: 'linear-gradient(135deg, #faf2d2 0%, #eeeeee 50%, #e1f5f3 100%)',
    zIndex: -1,
  },
});

const Card = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 760,
  background: '#fff',
  borderRadius: 20,
  padding: '56px 56px 44px',
  boxShadow: '0 16px 36px rgba(46,196,182,0.25)',
  border: '2px solid rgba(46,196,182,0.35)',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: { padding: '32px 22px 28px', borderRadius: 16 },
}));

const AccentStrip = styled(Box)({
  position: 'absolute',
  top: 0, left: 0, right: 0, height: 6,
  background: 'linear-gradient(90deg, #ffe066 0%, #2ec4b6 100%)',
});

const ReadyFlag = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.78rem', letterSpacing: '0.32em',
  color: '#2ec4b6', fontWeight: 700, textTransform: 'uppercase',
  textAlign: 'center', marginBottom: 4,
});

const GameTitle = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  color: '#222', textAlign: 'center', lineHeight: 1.1,
  marginTop: 2, marginBottom: 8, wordBreak: 'break-word',
});

const Subhead = styled(Typography)({
  fontSize: '1.05rem', color: '#555', textAlign: 'center',
  lineHeight: 1.4, maxWidth: 520, margin: '4px auto 28px',
});

const StatsRow = styled(Box)({
  display: 'flex', justifyContent: 'center', gap: 32,
  margin: '20px 0 28px', padding: '16px 0',
  borderTop: '1px dashed rgba(0,0,0,0.1)',
  borderBottom: '1px dashed rgba(0,0,0,0.1)',
});

const Stat = styled(Box)({ textAlign: 'center' });

const StatNum = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '1.8rem', color: '#1a7a72', fontWeight: 800, lineHeight: 1,
});

const StatLabel = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.7rem', letterSpacing: '0.24em',
  color: '#888', textTransform: 'uppercase',
  marginTop: 4, fontWeight: 700,
});

const PrimaryCTA = styled(Button)({
  display: 'block', width: '100%', maxWidth: 360,
  margin: '0 auto', padding: '18px 24px',
  background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
  color: '#fff', borderRadius: 14,
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '1.05rem', letterSpacing: '0.18em',
  textTransform: 'uppercase', fontWeight: 800,
  boxShadow: '0 8px 20px rgba(46,196,182,0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 28px rgba(46,196,182,0.45)',
  },
});

const SecondaryRow = styled(Box)(({ theme }) => ({
  display: 'flex', justifyContent: 'center',
  gap: 28, marginTop: 24,
  [theme.breakpoints.down('sm')]: { flexDirection: 'column', gap: 12, alignItems: 'center' },
}));

const SecondaryLink = styled(MuiLink)({
  fontSize: '0.85rem', color: '#1a7a72', cursor: 'pointer',
  textDecoration: 'none', borderBottom: '1px solid rgba(46,196,182,0.4)',
  paddingBottom: 1, fontWeight: 600,
  '&:hover': { color: '#2ec4b6', borderBottomColor: '#2ec4b6' },
});

const buildSubhead = ({ categoryCount, questionCount, hasFinale }) => {
  const base = `${categoryCount} categories. ${questionCount} questions.`;
  return hasFinale ? `${base} One epic finale.` : base;
};

const GameCompleteChoice = ({ gameId, gameName, categoryCount = 0, questionCount = 0, roundCount = 1, hasFinale = false }) => {
  const navigate = useNavigate();
  const titleFontSize = useAdaptiveFontSize(gameName, { baseRem: 2.8, minRem: 1.6 });

  const handlePlay = () => {
    log.info('Play Now clicked', { gameId });
    navigate(`/gameboard/${gameId}`);
  };
  const handleEdit = () => navigate(`/manage/game/${gameId}/details`);
  const handleSave = () => navigate('/manage');

  return (
    <Root>
      <Card>
        <AccentStrip />
        <BrandLockup size="md" variant="light" sx={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }} />
        <ReadyFlag>Ready to Play</ReadyFlag>
        <GameTitle sx={{ fontSize: titleFontSize }}>{gameName}</GameTitle>
        <Subhead>{buildSubhead({ categoryCount, questionCount, hasFinale })}</Subhead>

        <StatsRow>
          <Stat><StatNum>{categoryCount}</StatNum><StatLabel>Categories</StatLabel></Stat>
          <Stat><StatNum>{questionCount}</StatNum><StatLabel>Questions</StatLabel></Stat>
          <Stat><StatNum>{roundCount}</StatNum><StatLabel>Rounds</StatLabel></Stat>
        </StatsRow>

        <PrimaryCTA onClick={handlePlay}>Play Now</PrimaryCTA>

        <SecondaryRow>
          <SecondaryLink onClick={handleEdit}>Edit game first</SecondaryLink>
          <SecondaryLink onClick={handleSave}>Save for later</SecondaryLink>
        </SecondaryRow>
      </Card>
    </Root>
  );
};

export default GameCompleteChoice;
